import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@mmfestival/shared-components/common/NotificationContext";
import { InputField } from "@mmfestival/shared-components";
import {
  getFirestore,
  collection,
  onSnapshot,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  getDocs,
  getDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { format, parse } from "date-fns";
import { Copy, Trash2, Plus, Users, X } from "lucide-react";

export const VoucherManagement = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [isAdmin, setIsAdmin] = useState(false);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const db = getFirestore();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    code: "",
    discountPercentage: "100",
    maxUsage: "",
    allowedCategories: "",
    expiredAt: "",
  });

  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [usersWhoUsed, setUsersWhoUsed] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Admin check
  useEffect(() => {
    if (!user) {
      showNotification("error", "Please log in as admin");
      navigate("/Login");
      return;
    }
    const check = async () => {
      const { getDoc } = await import("firebase/firestore");
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists() && snap.data()?.role === "admin") {
        setIsAdmin(true);
      } else {
        showNotification("error", "Access denied");
        navigate("/");
      }
    };
    check();
  }, [user, db, navigate, showNotification]);

  // Load vouchers
  useEffect(() => {
    if (!isAdmin) return;
    const q = query(collection(db, "vouchers"), orderBy("code"));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => {
        const data = d.data();
        let dateValue = "";
        if (data.expiredAtHuman) {
          try {
            const parsed = parse(data.expiredAtHuman, "dd/MM/yy", new Date());
            dateValue = format(parsed, "yyyy-MM-dd");
          } catch (e) {
            console.warn("Invalid date format:", data.expiredAtHuman);
          }
        }
        return { id: d.id, ...data, _dateValue: dateValue };
      });
      setVouchers(list);
      setLoading(false);
    });
    return unsub;
  }, [isAdmin, db]);

  // Create voucher
  const handleCreateVoucher = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) {
      showNotification("error", "Voucher code is required");
      return;
    }

    let expiredAtHuman = null;
    if (form.expiredAt) {
      expiredAtHuman = format(new Date(form.expiredAt), "dd/MM/yy");
    }

    try {
      await setDoc(doc(db, "vouchers", form.code.toUpperCase()), {
        code: form.code.toUpperCase(),
        discountPercentage: Number(form.discountPercentage) || 100,
        maxUsage: form.maxUsage ? Number(form.maxUsage) : null,
        allowedCategories: form.allowedCategories
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean),
        expiredAtHuman,
        isActive: true,
        usageCount: 0,
        createdAt: serverTimestamp(),
      });
      showNotification(
        "success",
        `Voucher ${form.code.toUpperCase()} created!`
      );
      setForm({
        code: "",
        discountPercentage: "100",
        maxUsage: "",
        allowedCategories: "",
        expiredAt: "",
      });
    } catch (err) {
      showNotification("error", "Failed to create voucher");
    }
  };

  const handleExpiryChange = async (code, isoDate) => {
    let expiredAtHuman = null;
    if (isoDate) {
      expiredAtHuman = format(new Date(isoDate), "dd/MM/yy");
    }
    await updateDoc(doc(db, "vouchers", code), { expiredAtHuman });
  };

  const toggleActive = (code, current) =>
    updateDoc(doc(db, "vouchers", code), { isActive: !current });

  const deleteVoucher = async (code) => {
    if (!window.confirm(`Delete voucher ${code} permanently?`)) return;
    await deleteDoc(doc(db, "vouchers", code));
    showNotification("success", `${code} deleted`);
  };

  const getVoucherStatus = (v) => {
    if (v.maxUsage && (v.usageCount || 0) >= v.maxUsage) return "Usage Limit";

    if (v.expiredAtHuman) {
      try {
        const parsed = parse(v.expiredAtHuman, "dd/MM/yy", new Date());
        // Set time to end of day for fair comparison
        parsed.setHours(23, 59, 59, 999);
        if (parsed < new Date()) return "Inactive";
      } catch (e) {
        // ignore
      }
    }

    if (!v.isActive) return "Inactive";

    return "Active";
  };

  const CopyButton = ({ code }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <button
        onClick={handleCopy}
        className={`transition ${
          copied ? "text-green-600" : "text-slate-400 hover:text-indigo-600"
        }`}
        title={copied ? "Copied!" : "Copy Code"}
      >
        {copied ? (
          <span className="text-xs font-bold">Copied!</span>
        ) : (
          <Copy size={16} />
        )}
      </button>
    );
  };

  // FINAL: Lihat pengguna voucher + ambil nomor HP dari users
  const showUsersWhoUsed = async (voucherCode) => {
    setSelectedVoucher(voucherCode);
    setLoadingUsers(true);
    setUsersWhoUsed([]);

    try {
      const usedByRef = collection(db, "vouchers", voucherCode, "usedBy");
      const q = query(usedByRef, orderBy("usedAt", "desc"));
      const snap = await getDocs(q);

      const users = [];

      for (const d of snap.docs) {
        const data = d.data();

        const userInfo = {
          name: data.namaLengkap || "Anonymous",
          email: data.email || "-",
          phone: "-",
          category: data.category || "-",
          eventId: data.eventId || "-",
          usedAt: data.usedAt?.toDate() || new Date(),
        };

        // Ambil nomor HP dari collection users jika ada uid
        if (data.uid) {
          try {
            const userDoc = await getDoc(doc(db, "users", data.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              if (userData.telepon) userInfo.phone = userData.telepon;
              if (userData.namaLengkap) userInfo.name = userData.namaLengkap;
              if (userData.email) userInfo.email = userData.email;
            }
          } catch (err) {
            console.warn("Failed to load user data:", data.uid);
          }
        }

        users.push(userInfo);
      }

      setUsersWhoUsed(users);
    } catch (err) {
      console.error("Error loading usedBy:", err);
      showNotification("error", "Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <section className="w-full min-h-screen py-20 bg-white text-slate-700">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <h1 className="text-3xl font-black text-slate-800 mb-10">
          Admin - Voucher Management
        </h1>

        {/* Form Create */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-6 mb-8"
        >
          <h2 className="text-2xl font-black text-slate-700 mb-6">
            Create New Voucher
          </h2>
          <form
            onSubmit={handleCreateVoucher}
            className="grid md:grid-cols-2 gap-6"
          >
            <InputField
              label="Voucher Code"
              name="code"
              value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }
              placeholder="e.g. BMM45-001"
              required
              labelColor="text-slate-700"
            />
            <InputField
              label="Discount Percentage"
              name="discountPercentage"
              type="number"
              min="1"
              max="100"
              value={form.discountPercentage}
              onChange={(e) =>
                setForm({ ...form, discountPercentage: e.target.value })
              }
              labelColor="text-slate-700"
            />
            <InputField
              label="Max Usage (leave blank = unlimited)"
              name="maxUsage"
              type="number"
              min="1"
              value={form.maxUsage}
              onChange={(e) => setForm({ ...form, maxUsage: e.target.value })}
              placeholder="e.g. 50"
              labelColor="text-slate-700"
            />
            <InputField
              label="Allowed Categories (comma separated)"
              name="allowedCategories"
              value={form.allowedCategories}
              onChange={(e) =>
                setForm({ ...form, allowedCategories: e.target.value })
              }
              placeholder="e.g. BMM 45K"
              labelColor="text-slate-700"
            />
            <InputField
              label="Expiry Date (optional)"
              name="expiredAt"
              type="date"
              value={form.expiredAt}
              onChange={(e) => setForm({ ...form, expiredAt: e.target.value })}
              labelColor="text-slate-700"
            />
            <div className="md:col-span-2 flex justify-center">
              <button
                type="submit"
                className="px-8 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-800 transition font-semibold flex items-center gap-2"
              >
                <Plus size={20} /> Create Voucher
              </button>
            </div>
          </form>
        </motion.div>

        {/* Voucher List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-slate-200 overflow-hidden"
        >
          <div className="p-6 bg-slate-50 border-b border-slate-200">
            <h2 className="text-2xl font-black text-slate-800">
              Voucher List ({vouchers.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Discount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Categories
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-12 text-slate-500"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : vouchers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-12 text-slate-500"
                    >
                      No vouchers yet
                    </td>
                  </tr>
                ) : (
                  vouchers.map((v) => {
                    const status = getVoucherStatus(v);
                    return (
                      <tr key={v.code} className="hover:bg-slate-50 transition">
                        <td className="px-6 py-4 font-mono font-bold text-indigo-600">
                          <div className="flex items-center gap-3">
                            {v.code}
                            <CopyButton code={v.code} />
                            <button
                              onClick={() => showUsersWhoUsed(v.code)}
                              className="text-slate-400 hover:text-blue-600"
                              title="View users"
                            >
                              <Users size={16} />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4">{v.discountPercentage}%</td>
                        <td className="px-6 py-4">
                          {v.usageCount || 0} / {v.maxUsage || "Unlimited"}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {v.allowedCategories?.join(", ") || "-"}
                        </td>
                        <td className="px-6 py-4">
                          <InputField
                            name={`expire-${v.code}`}
                            type="date"
                            value={v._dateValue || ""}
                            onChange={(e) =>
                              handleExpiryChange(v.code, e.target.value)
                            }
                            labelColor="text-slate-700"
                            labelClassName="sr-only"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <button
                          onClick={() => toggleActive(v.code, v.isActive)}
                          className={`px-4 py-2 rounded-full text-xs font-bold transition ${
                            status === "Active"
                              ? "bg-green-100 text-green-800 hover:bg-green-200"
                              : status === "Usage Limit"
                              ? "bg-gray-100 text-gray-800"
                              : "bg-red-100 text-red-800 hover:bg-red-200"
                          }`}
                        >
                          {status}
                        </button>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => deleteVoucher(v.code)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Modal Users */}
        {selectedVoucher && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-xl border border-slate-200 max-w-2xl w-full max-h-[80vh] overflow-y-auto p-4"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-black text-slate-800">
                  Users of Voucher: {selectedVoucher}
                </h3>
                <button
                  onClick={() => setSelectedVoucher(null)}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X size={28} />
                </button>
              </div>

              {loadingUsers ? (
                <p className="text-center py-8 text-slate-600">
                  Loading users...
                </p>
              ) : usersWhoUsed.length === 0 ? (
                <p className="text-center py-8 text-slate-500">
                  No one has used this voucher yet
                </p>
              ) : (
                <div className="space-y-4">
                  {usersWhoUsed.map((u, i) => (
                    <div
                      key={i}
                      className="border border-slate-200 rounded-xl p-5 bg-slate-50"
                    >
                      <div className="font-bold text-slate-800">{u.name}</div>
                      <div className="text-sm text-slate-600">
                        Email: {u.email}
                      </div>
                      <div className="text-sm text-slate-600">
                        Phone: {u.phone}
                      </div>
                      <div className="text-sm text-slate-600">
                        Category: {u.category}
                      </div>
                      <div className="text-sm text-slate-600">
                        Event: {u.eventId}
                      </div>
                      <div className="text-xs text-slate-500 mt-2">
                        Used on: {format(u.usedAt, "dd MMM yyyy HH:mm")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};

export default VoucherManagement;
